import { useEffect } from 'react';
import { pushRecentFile, type RecentFileEntry } from '@munifocus/contracts-gov';

/**
 * The files this browser opened most recently, for the palette's "Recent files"
 * group. A per-viewer convenience only: stored locally, wrapped in try/catch so
 * a private window or blocked storage simply shows no recents.
 *
 * Holds only what the palette prints — reference and address of files the
 * officer has already opened — and is cleared on sign-out with the rest of the
 * session's local state.
 */
export const RECENT_FILES_KEY = 'gov.recentFiles';

export function readRecentFiles(): RecentFileEntry[] {
  try {
    const raw = localStorage.getItem(RECENT_FILES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentFileEntry =>
        !!e &&
        typeof e === 'object' &&
        typeof (e as RecentFileEntry).id === 'string' &&
        typeof (e as RecentFileEntry).reference === 'string' &&
        typeof (e as RecentFileEntry).address === 'string',
    );
  } catch {
    return [];
  }
}

export function recordRecentFile(entry: Omit<RecentFileEntry, 'viewedAt'>): void {
  try {
    const next = pushRecentFile(readRecentFiles(), {
      ...entry,
      viewedAt: new Date().toISOString(),
    });
    localStorage.setItem(RECENT_FILES_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable; recents are a convenience.
  }
}

export function forgetRecentFiles(): void {
  try {
    localStorage.removeItem(RECENT_FILES_KEY);
  } catch {
    // Nothing remembered to forget.
  }
}

/** Record a file as viewed once its detail has loaded. */
export function useRecordRecentFile(
  file: { id: string; reference: string; address: string } | null | undefined,
): void {
  const id = file?.id;
  const reference = file?.reference;
  const address = file?.address;
  useEffect(() => {
    if (id && reference && address !== undefined) recordRecentFile({ id, reference, address });
  }, [id, reference, address]);
}
